import type {
  AgentActivityEntry,
  AgentActivityProtocol,
  AgentActivityResolvedRisk,
  AgenticClientListItem,
} from "@p0security/integrations-agentic-shared/interface";
import { AppPaths } from "@p0security/shared/routes/constants";
import {
  Card,
  Descriptions,
  Drawer,
  Flex,
  Spacing,
  Tag,
  Typography,
} from "@p0security/ui";
import {
  DelegationChain,
  delegatesOf,
} from "components/Agentic/DelegationChain";
import {
  McpClientIdIcon,
  mcpClientDisplayName,
} from "components/Agentic/clientPlatforms";
import { DetailsDescriptions } from "components/Jit/DetailsDescriptions";
import type { ResizableTableColumnsType } from "components/ResizableTable";
import { Identity } from "components/common/Identity";
import { TenantAwareLink } from "components/common/TenantAwareLink";
import { isValid, parseISO } from "date-fns";

import { JitPaths } from "../../Jit/paths.ts";
import { attributionOf } from "./attribution.ts";
import { EventTitle } from "./components/EventTitle.tsx";
import { RiskTag } from "./components/RiskTag.tsx";
import { eventProtocolViews, viewOf } from "./eventProtocols.tsx";

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
const TIME_FORMAT = new Intl.DateTimeFormat(undefined, { timeStyle: "medium" });
const formatTimestamp = (timestamp: string) => {
  const parsed = parseISO(timestamp);
  return isValid(parsed)
    ? { date: DATE_FORMAT.format(parsed), time: TIME_FORMAT.format(parsed) }
    : { date: timestamp, time: undefined };
};

const EventTimestamp: React.FC<{ timestamp: string }> = ({ timestamp }) => {
  const formatted = formatTimestamp(timestamp);
  return (
    <time className="flex flex-col" dateTime={timestamp}>
      <Typography.Text>{formatted.date}</Typography.Text>
      {formatted.time && (
        <Typography.Text className="text-xs" type="secondary">
          {formatted.time}
        </Typography.Text>
      )}
    </time>
  );
};

const AccessRequestLink: React.FC<{
  requestId?: string;
  truncate?: boolean;
}> = ({ requestId, truncate }) => {
  if (!requestId) return <Typography.Text type="secondary">—</Typography.Text>;
  const link = (
    <TenantAwareLink
      to={`${AppPaths.AccessManagement}/${JitPaths.History}/${encodeURIComponent(requestId)}`}
    >
      {requestId}
    </TenantAwareLink>
  );
  return truncate ? (
    <Typography.Text className="block" ellipsis={{ tooltip: requestId }}>
      {link}
    </Typography.Text>
  ) : (
    link
  );
};

const ProtocolTag: React.FC<{ protocol: AgentActivityProtocol }> = ({
  protocol,
}) => {
  const { label, tagColor } = eventProtocolViews[protocol];
  return (
    <Spacing asChild m={0}>
      <Tag color={tagColor}>{label}</Tag>
    </Spacing>
  );
};

/** Marks a call the gateway's entitlement gate rejected. Such a call was
 * logged and never forwarded, so it is the only record of the attempt. */
const BlockedTag: React.FC<{ allowed?: boolean }> = ({ allowed }) =>
  allowed === false ? (
    <Spacing asChild m={0}>
      <Tag color="red">Blocked</Tag>
    </Spacing>
  ) : null;

const CallSummary: React.FC<AgentActivityEntry> = (entry) => {
  const { allowed, protocol } = entry;
  const view = viewOf(entry);
  const name = view.callName(entry);
  const detail = view.callDetail(entry);
  const serverName = view.serverName(entry);

  return (
    <Flex className="min-w-0" gap={2} vertical>
      <Flex className="min-w-0" align="center" gap={6}>
        {view.icon("shrink-0")}
        <Typography.Text
          className="block max-w-full"
          ellipsis={{ tooltip: name }}
          strong
        >
          {name ?? view.unnamedLabel}
        </Typography.Text>
        <ProtocolTag protocol={protocol} />
        <BlockedTag allowed={allowed} />
      </Flex>
      {serverName && (
        <Typography.Text
          className="block max-w-full text-xs"
          ellipsis={{ tooltip: serverName }}
          type="secondary"
        >
          {serverName}
        </Typography.Text>
      )}
      {detail && (
        <Typography.Text
          className="block max-w-full font-mono text-xs"
          ellipsis={{ tooltip: detail }}
          type="secondary"
        >
          {detail}
        </Typography.Text>
      )}
    </Flex>
  );
};

const EventHeading: React.FC<AgentActivityEntry> = (entry) => {
  const { allowed, direction, messageKind, method, protocol } = entry;
  const name = viewOf(entry).callName(entry);
  const primary = name ?? method ?? messageKind;
  const details = [
    name ? method : undefined,
    direction,
    messageKind === primary ? undefined : messageKind,
  ].filter((value): value is string => Boolean(value));

  return primary ? (
    <Flex gap={2} vertical>
      <Flex align="center" gap={6}>
        {name && viewOf(entry).icon()}
        <Typography.Text ellipsis={{ tooltip: primary }} strong={Boolean(name)}>
          {primary}
        </Typography.Text>
        <ProtocolTag protocol={protocol} />
        <BlockedTag allowed={allowed} />
      </Flex>
      {details.length > 0 && (
        <Typography.Text className="font-mono text-xs" type="secondary">
          {details.join(" · ")}
        </Typography.Text>
      )}
    </Flex>
  ) : (
    <Typography.Text type="secondary">Unknown</Typography.Text>
  );
};

const DetailValue: React.FC<{ copyable?: boolean; value?: string }> = ({
  copyable,
  value,
}) =>
  value ? (
    <Typography.Text
      className="break-all font-mono text-xs"
      copyable={copyable ? { text: value } : undefined}
    >
      {value}
    </Typography.Text>
  ) : (
    <Typography.Text type="secondary">—</Typography.Text>
  );

/** Safely serializes event data for display and local search. */
export const stringifyJson = (value: unknown, space?: number) => {
  try {
    return JSON.stringify(value, null, space) ?? "";
  } catch {
    return "";
  }
};

const EventArguments: React.FC<{
  arguments: Record<string, unknown>;
}> = ({ arguments: args }) => {
  const formatted = stringifyJson(args, 2);
  return (
    <Flex gap={8} vertical>
      <Flex align="center" justify="space-between">
        <Typography.Text strong>Request Arguments</Typography.Text>
        <Typography.Text copyable={{ text: formatted }} type="secondary">
          JSON
        </Typography.Text>
      </Flex>
      <Spacing asChild m={0}>
        <pre
          aria-label="Request Arguments"
          className="max-h-80 overflow-auto whitespace-pre-wrap break-words bg-transparent p-0 font-mono text-xs"
        >
          {formatted}
        </pre>
      </Spacing>
    </Flex>
  );
};

/** Displays the full details for a selected event row. */
export const EventDetailsDrawer: React.FC<{
  clients: AgenticClientListItem[];
  entry?: AgentActivityEntry;
  onClose: () => void;
}> = ({ clients, entry, onClose }) => {
  // Identical to the request view: the delegation chain the API returns, then
  // the caller as the subject it acted for.
  const delegates = entry ? delegatesOf(entry.actors ?? [], entry.caller) : [];
  return (
    <Drawer
      destroyOnHidden
      open={Boolean(entry)}
      title={entry ? <EventTitle entry={entry} /> : "Event Details"}
      width="75%"
      onClose={onClose}
    >
      {entry && (
        <Flex gap={20} vertical>
          <EventHeading {...entry} />
          <DetailsDescriptions>
            <Descriptions.Item label="Time">
              <EventTimestamp timestamp={entry.timestamp} />
            </Descriptions.Item>
            <Descriptions.Item label="Protocol">
              <ProtocolTag protocol={entry.protocol} />
            </Descriptions.Item>
            <Descriptions.Item label={viewOf(entry).serverLabel}>
              <DetailValue value={viewOf(entry).serverName(entry)} />
            </Descriptions.Item>
            {entry.agentId ? (
              <Descriptions.Item label="Agent Name">
                <DetailValue value={entry.agentId} />
              </Descriptions.Item>
            ) : null}
            <Descriptions.Item label="Principal">
              <DetailValue value={entry.caller} />
            </Descriptions.Item>
            <Descriptions.Item label="Client ID">
              <Flex align="center" gap={6}>
                <McpClientIdIcon clientId={entry.clientId} />
                <DetailValue copyable value={entry.clientId} />
              </Flex>
            </Descriptions.Item>
            {mcpClientDisplayName(clients, entry.clientId) !==
              entry.clientId && (
              <Descriptions.Item label="Client Display Name">
                <Typography.Text className="break-words">
                  {mcpClientDisplayName(clients, entry.clientId)}
                </Typography.Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Session ID">
              <DetailValue copyable value={entry.sessionId} />
            </Descriptions.Item>
            {/* span={2} ensures the next item (the chain) has room to span */}
            <Descriptions.Item label="Access Request" span={2}>
              <AccessRequestLink requestId={entry.requestId} />
            </Descriptions.Item>
            <Descriptions.Item label="Delegation Chain" span={2}>
              <DelegationChain delegates={delegates} />
            </Descriptions.Item>
          </DetailsDescriptions>
          <Card title="Observability">
            <DetailsDescriptions>
              <Descriptions.Item label="Log Entry ID">
                <DetailValue copyable value={entry.id} />
              </Descriptions.Item>
              {entry.trace && (
                <>
                  <Descriptions.Item label="Trace ID">
                    <DetailValue copyable value={entry.trace.traceId} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Span ID">
                    <DetailValue copyable value={entry.trace.spanId} />
                  </Descriptions.Item>
                  <Descriptions.Item label="Trace Sampling">
                    <Typography.Text>
                      {entry.trace.sampled ? "Sampled" : "Not sampled"}
                    </Typography.Text>
                  </Descriptions.Item>
                </>
              )}
            </DetailsDescriptions>
          </Card>
          {entry.arguments && <EventArguments arguments={entry.arguments} />}
        </Flex>
      )}
    </Drawer>
  );
};

const ClientSummary: React.FC<
  { clients: AgenticClientListItem[] } & Pick<
    AgentActivityEntry,
    "actors" | "caller" | "clientId"
  >
> = ({ actors, caller, clients, clientId }) => {
  if (!clientId) return <Typography.Text type="secondary">—</Typography.Text>;

  const displayName = mcpClientDisplayName(clients, clientId);
  const hasDisplayName = displayName !== clientId;
  // The subject is the chain's own principal, shown unprefixed; clientId is
  // what it acted through, so it's clientId's "via" here, not the reverse.
  const subject = attributionOf(actors, caller);
  return (
    <Flex align="center" className="min-w-0" gap={6}>
      <McpClientIdIcon clientId={clientId} />
      <div className="flex min-w-0 flex-col">
        {subject ? (
          <>
            <Typography.Text
              className="block min-w-0 text-xs"
              ellipsis={{ tooltip: subject.sub }}
            >
              <Identity value={subject} />
            </Typography.Text>
            <Typography.Text
              className="block min-w-0 text-xs leading-snug"
              ellipsis={{ tooltip: clientId }}
              type="secondary"
            >
              {/* clientId always identifies an OAuth-registered agent
                  client, never a human. */}
              <Identity
                value={{ role: "delegate", sub: clientId, type: "agent" }}
              />
            </Typography.Text>
          </>
        ) : (
          <>
            <Typography.Text
              className={`block min-w-0 text-xs${hasDisplayName ? "" : " font-mono"}`}
              ellipsis={{ tooltip: displayName }}
            >
              {displayName}
            </Typography.Text>
            {hasDisplayName && (
              <Typography.Text
                className="block min-w-0 font-mono text-xs leading-snug"
                ellipsis={{ tooltip: clientId }}
                type="secondary"
              >
                {clientId}
              </Typography.Text>
            )}
          </>
        )}
      </div>
    </Flex>
  );
};

/** Column definitions for the compact agentic event log table. */
export const eventColumns = (
  clients: AgenticClientListItem[]
): ResizableTableColumnsType<AgentActivityEntry> => [
  {
    dataIndex: "timestamp",
    key: "timestamp",
    preventResize: true,
    render: (timestamp: string) => <EventTimestamp timestamp={timestamp} />,
    title: "Time",
    width: 120,
  },
  {
    dataIndex: "resolvedRisk",
    key: "risk",
    preventResize: true,
    render: (resolvedRisk: AgentActivityResolvedRisk | undefined) => (
      <RiskTag resolvedRisk={resolvedRisk} />
    ),
    title: "Risk",
    width: 140,
  },
  {
    dataIndex: "sessionId",
    key: "sessionId",
    preventResize: true,
    render: (sessionId: string | undefined) =>
      sessionId ? sessionId.split("-")[0] : "—",
    title: "Session ID",
    width: 105,
  },
  {
    key: "toolCall",
    render: (_, entry) => <CallSummary {...entry} />,
    title: "Tool / Agent",
    width: 440,
  },
  {
    key: "clientId",
    render: (_, entry) => <ClientSummary clients={clients} {...entry} />,
    responsive: ["lg"],
    title: "Identity",
    width: 380,
  },
  {
    dataIndex: "requestId",
    key: "requestId",
    preventResize: true,
    render: (_: unknown, entry) => (
      <AccessRequestLink truncate requestId={entry.requestId} />
    ),
    responsive: ["xl"],
    title: "Access Request",
    width: 200,
  },
];
